import React, { useMemo, useState } from 'react';
import { AlertTriangle, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import { getEventDetailStatus } from '../lib/eventCompleteness';
import { parseLocalDate } from '../lib/dateHelpers';

/**
 * Reception follow-ups (Oct 2026) — top of the home page.
 * RED: bookings within 7 days with details still missing (do these now).
 * ORANGE: provisional bookings further out; listed so reception can get
 * ahead, each also has an auto task due 7 days before the event.
 */
const fmtDay = (k) => parseLocalDate(k)?.toLocaleDateString('en-IE', { weekday: 'short', day: 'numeric', month: 'short' }) || k;

export default function ReceptionFollowUps({ onSelectEvent }) {
  const { events = [] } = useAppContext();
  const [showOrange, setShowOrange] = useState(false);

  const { red, orange } = useMemo(() => {
    const red = []; const orange = [];
    events.forEach((e) => {
      const st = getEventDetailStatus(e);
      if (st.status === 'urgent') red.push({ e, st });
      else if (st.status === 'provisional') orange.push({ e, st });
    });
    const byDate = (a, b) => (a.e.date || '').localeCompare(b.e.date || '');
    return { red: red.sort(byDate), orange: orange.sort(byDate) };
  }, [events]);

  if (!red.length && !orange.length) return null;

  const row = ({ e, st }, tone) => (
    <button key={e.id} type="button" onClick={() => onSelectEvent?.(e)}
      className={`w-full text-left flex flex-wrap items-baseline gap-x-2 gap-y-0.5 px-2 py-1.5 rounded-lg hover:bg-white/70 text-sm ${tone === 'red' ? 'text-red-900' : 'text-orange-900'}`}>
      <span className="font-semibold">{fmtDay(e.date)}</span>
      <span className="font-semibold truncate max-w-[16rem]">{e.name || 'Untitled event'}</span>
      <span className="text-xs opacity-80">{st.days === 0 ? 'today' : `in ${st.days} day${st.days === 1 ? '' : 's'}`}</span>
      <span className="text-xs basis-full opacity-90">Missing: {st.missing.map((m) => m.label.replace(/ \(.*\)$/, '')).join(', ')}</span>
    </button>
  );

  return (
    <div className="mb-4 rounded-xl border overflow-hidden border-red-200">
      {red.length > 0 && (
        <div className="bg-red-50 px-3 py-2.5">
          <div className="flex items-center gap-2 text-red-800 font-bold text-sm mb-1">
            <AlertTriangle size={16} /> Reception: {red.length} booking{red.length === 1 ? '' : 's'} within 7 days missing details — complete today
          </div>
          <div className="space-y-0.5">{red.map((x) => row(x, 'red'))}</div>
        </div>
      )}
      {orange.length > 0 && (
        <div className="bg-orange-50 px-3 py-2 border-t border-orange-200">
          <button type="button" onClick={() => setShowOrange((v) => !v)} className="w-full flex items-center gap-2 text-orange-800 font-semibold text-sm">
            <Clock size={15} /> {orange.length} provisional booking{orange.length === 1 ? '' : 's'} further out still need details
            <span className="ml-auto">{showOrange ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</span>
          </button>
          {showOrange && <div className="space-y-0.5 mt-1 max-h-72 overflow-y-auto">{orange.map((x) => row(x, 'orange'))}</div>}
        </div>
      )}
    </div>
  );
}
